import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import CustomTablePagination from 'components/CustomTablePagination';
import { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { EmptyMessage, Load, LoadCenter } from 'ui/styled';
import { BotaoExpandir, TabledRowText } from './styled';

const BORDER_GAP = 4;
// --- Estilos ---
// Maiko Rodrigo Defreyn
// ---------------------------------------------------------
// Novo estilo com colunas fixas e background branco entre colunas mas sem espaçamento entre linhas
const StyledTable = styled(Table)({
  tableLayout: 'auto',
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: '0',
});

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'fixed' && prop !== 'left',
})(({ fixed, left }) => ({
  position: 'relative',
  borderRight: `${BORDER_GAP}px solid #ffffff !important`,
  backgroundClip: 'padding-box !important',

  [`&.${tableCellClasses.head}`]: {
    whiteSpace: 'nowrap',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    fontFamily: 'Kumbh Sans',
    position: 'sticky',
    top: 0,
    zIndex: fixed ? 20 : 10,
    backgroundColor: '#d5d5d5ff',

    ...(fixed && { left }),
  },

  [`&.${tableCellClasses.body}`]: {
    BorderColor: '#ffffff',
    Border: '4px',
    whiteSpace: 'nowrap',
    fontSize: 14,
    fontWeight: 400,
    fontFamily: 'Kumbh Sans',
    padding: '8px 12px',
    marginBottom: `${BORDER_GAP}px`,
    backgroundColor: 'inherit',

    ...(fixed && {
      position: 'sticky',
      left,
      zIndex: 11,
      backgroundColor: '#d5d5d5ff',
    }),
  },
}));
// ---------------------------------------------------------

// Coluna extra do expandir: so existe quando ha agrupamento.
const CelulaExpandir = styled(StyledTableCell)({
  width: 40,
  padding: '0 4px',
  textAlign: 'center',
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.colors.lightshadow,
  },
  '&:nth-of-type(even)': {
    backgroundColor: theme.palette.colors.light,
  },
}));

// Linha filha de um grupo expandido: fundo unico (nao alterna) e uma barra a
// esquerda, para ficar claro que pertence a linha de cima.
const SubTableRow = styled(TableRow)({
  backgroundColor: '#fbfcfe',
  boxShadow: 'inset 3px 0 0 #2670D1',
});

// --- Funções Auxiliares ---
const isEmptyValue = (value) =>
  value === null || value === undefined || value === '';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  const compare =
    order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);

  // Linha sem valor vai sempre para o fim, nas duas direcoes. Sem este passo
  // ela "empata" com todas as outras — `undefined < x` e `undefined > x` sao
  // ambos false, entao o comparador devolve 0 —, e o stableSort mantem a
  // ordem de entrada: numa coluna pouco preenchida a lista nem se mexia.
  // Fica fora do descendingComparator de proposito: o ramo asc inverte o
  // sinal, e o vazio subiria para o topo em vez de ficar no fim.
  return (a, b) => {
    const emptyA = isEmptyValue(a?.[orderBy]);
    const emptyB = isEmptyValue(b?.[orderBy]);
    if (emptyA && emptyB) {
      return 0;
    }
    if (emptyA) {
      return 1;
    }
    if (emptyB) {
      return -1;
    }
    return compare(a, b);
  };
}

// --- Componente Principal ---
export default function BasicTable({
  columns,
  rows,
  loading,
  pagination,
  fixedColumns = 0,
  // Nome do campo que identifica o grupo. Com ele, linhas que compartilham
  // esse valor viram uma linha so, expansivel. Sem ele nada muda.
  groupBy,
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [gruposAbertos, setGruposAbertos] = useState({});

  // Maiko Defreyn
  // ---------------------------------------------------------
  const [fixedOffsets, setFixedOffsets] = useState([]);
  const headerRefs = useRef([]);

  // Cálculo dinâmico do tamanho das colunas no render inicial
  useLayoutEffect(() => {
    if (!fixedColumns) {
      setFixedOffsets([]);
      return;
    }
    const offsets = [];
    let left = 0;
    for (let i = 0; i < fixedColumns; i++) {
      const cell = headerRefs.current[i];
      if (!cell) {
        continue;
      }
      offsets[i] = left;
      left += cell.offsetWidth;
    }
    setFixedOffsets(offsets);
  }, [columns, fixedColumns, rowsPerPage]);
  // ---------------------------------------------------------

  // Manipula mudança de página
  const handleChangePage = (event, newPage) => setPage(newPage);

  // Manipula mudança de linhas por página
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reseta para a primeira página
  };

  // Manipula ordenação
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Ordenação estável
  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Aplica ordenação
  const sortedRows = stableSort(rows || [], getComparator(order, orderBy));

  // Agrupa depois de ordenar: a ordem dos grupos segue a da primeira linha de
  // cada um, entao ordenar por uma coluna continua funcionando como antes.
  const grupos = useMemo(() => {
    if (!groupBy) {
      return null;
    }
    const mapa = new Map();
    sortedRows.forEach((row) => {
      const chave = row?.[groupBy];
      if (!mapa.has(chave)) {
        mapa.set(chave, { chave, row, rows: [] });
      }
      mapa.get(chave).rows.push(row);
    });
    return [...mapa.values()];
    // sortedRows e recalculado a cada render; as dependencias reais sao estas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, groupBy, order, orderBy]);

  // Com agrupamento, a paginacao conta grupos — nao linhas.
  const itens = grupos || sortedRows;

  // Aplica paginação
  const paginatedRows =
    rowsPerPage > 0
      ? itens.slice(page * rowsPerPage, (page + 1) * rowsPerPage)
      : itens;

  const algumAberto = Object.values(gruposAbertos).some(Boolean);

  // O botao do cabecalho abre ou fecha todos os grupos da pagina de uma vez.
  const alternarTodos = () => {
    if (algumAberto) {
      setGruposAbertos({});
      return;
    }
    const proximo = {};
    (grupos || []).forEach((grupo) => {
      if (grupo.rows.length > 1) {
        proximo[grupo.chave] = true;
      }
    });
    setGruposAbertos(proximo);
  };

  const alternarGrupo = (chave) =>
    setGruposAbertos((atual) => ({ ...atual, [chave]: !atual[chave] }));

  // Renderiza o corpo da tabela
  const TableContent = () => {
    if (loading) {
      return (
        <LoadCenter>
          <Load />
        </LoadCenter>
      );
    }

    if (!rows?.length) {
      return (
        <TableBody>
          <TableRow>
            <StyledTableCell
              colSpan={(columns?.length || 1) + (groupBy ? 1 : 0)}
              style={{ textAlign: 'center' }}
            >
              <EmptyMessage style={{ margin: '0px', fontSize: '15px' }}>
                Nenhum registro encontrado.
              </EmptyMessage>
            </StyledTableCell>
          </TableRow>
        </TableBody>
      );
    }
    const celula = (item, colIndex, conteudo) => (
      <StyledTableCell
        key={colIndex}
        align={item.align || 'left'}
        fixed={colIndex < fixedColumns}
        left={fixedOffsets[colIndex]}
      >
        {conteudo}
      </StyledTableCell>
    );

    const conteudoPadrao = (item, row) =>
      item.renderCell ? item.renderCell({ row }) : row?.[item.ref];

    if (!groupBy) {
      return (
        <TableBody>
          {paginatedRows.map((row, rowIndex) => (
            <StyledTableRow key={rowIndex}>
              {columns?.map((item, colIndex) =>
                celula(item, colIndex, conteudoPadrao(item, row))
              )}
            </StyledTableRow>
          ))}
        </TableBody>
      );
    }

    return (
      <TableBody>
        {paginatedRows.map((grupo) => {
          // Grupo de uma linha so nao tem o que expandir: vira linha comum.
          const agrupado = grupo.rows.length > 1;
          const aberto = !!gruposAbertos[grupo.chave];
          return (
            <Fragment key={grupo.chave}>
              <StyledTableRow>
                <CelulaExpandir>
                  {!agrupado ? null : (
                    <BotaoExpandir
                      type="button"
                      $aberto={aberto}
                      title={
                        aberto
                          ? 'Recolher parcelas'
                          : `Ver as ${grupo.rows.length} parcelas`
                      }
                      onClick={() => alternarGrupo(grupo.chave)}
                    >
                      <span className="material-symbols-outlined">
                        expand_more
                      </span>
                    </BotaoExpandir>
                  )}
                </CelulaExpandir>
                {columns?.map((item, colIndex) =>
                  celula(
                    item,
                    colIndex,
                    // Na linha do grupo a coluna decide o que mostrar; sem
                    // renderGroupCell cai no valor da linha representante.
                    agrupado && item.renderGroupCell
                      ? item.renderGroupCell({
                          row: grupo.row,
                          rows: grupo.rows,
                        })
                      : conteudoPadrao(item, grupo.row)
                  )
                )}
              </StyledTableRow>

              {!agrupado || !aberto
                ? null
                : grupo.rows.map((sub, subIndex) => (
                    <SubTableRow key={`${grupo.chave}-${subIndex}`}>
                      <CelulaExpandir />
                      {columns?.map((item, colIndex) =>
                        celula(
                          item,
                          colIndex,
                          // Coluna marcada como do grupo nao se repete nas
                          // filhas: o dado ja esta na linha de cima.
                          item.groupLevel ? null : conteudoPadrao(item, sub)
                        )
                      )}
                    </SubTableRow>
                  ))}
            </Fragment>
          );
        })}
      </TableBody>
    );
  };

  return (
    <>
      <TableContainer
        component={Paper}
        style={{
          position: 'relative',
          boxShadow: 'none',
          // Sem altura fixa: a tabela fica do tamanho do conteudo enquanto
          // couber e encolhe quando a tela acaba. O minHeight 0 e o que
          // permite esse encolhimento — sem ele o item de flex nao passa do
          // proprio conteudo e o scroll vaza para a pagina.
          minHeight: 0,
          maxHeight: 'none',
          overflowY: 'auto',
          overflowX: 'auto',
        }}
      >
        <StyledTable aria-label="custom table" sx={{ minWidth: 80 }}>
          <TableHead>
            <TableRow>
              {!groupBy ? null : (
                <CelulaExpandir>
                  <BotaoExpandir
                    type="button"
                    $aberto={algumAberto}
                    title={
                      algumAberto
                        ? 'Recolher todos os contratos'
                        : 'Expandir todos os contratos'
                    }
                    onClick={alternarTodos}
                  >
                    <span className="material-symbols-outlined">
                      expand_more
                    </span>
                  </BotaoExpandir>
                </CelulaExpandir>
              )}
              {columns?.map((item, key) => (
                <StyledTableCell
                  key={key}
                  ref={(el) => (headerRefs.current[key] = el)}
                  align={item.align || 'left'}
                  sortDirection={orderBy === item.ref ? order : false}
                  fixed={key < fixedColumns}
                  left={fixedOffsets[key]}
                >
                  {item.sortable ? (
                    <TableSortLabel
                      active={orderBy === item.ref}
                      direction={orderBy === item.ref ? order : 'asc'}
                      onClick={(event) => handleRequestSort(event, item.ref)}
                    >
                      <TabledRowText>{item.title}</TabledRowText>
                      {orderBy === item.ref && (
                        <span style={visuallyHidden}>
                          {order === 'desc'
                            ? 'sorted descending'
                            : 'sorted ascending'}
                        </span>
                      )}
                    </TableSortLabel>
                  ) : (
                    <TabledRowText>{item.title}</TabledRowText>
                  )}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          {TableContent()}
        </StyledTable>
      </TableContainer>

      {pagination && (
        <CustomTablePagination
          count={itens.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      )}
    </>
  );
}
